import { Flex, Text } from "@mantine/core";
import { InputDropdown } from "@onengine/component-library";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { Button } from "@/components/ui/Button/Button";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import type { WritableBox } from "@/api/access/userScope";
import { boxLabel, boxOptionsFor, newRule, noBoxLeftFor, type ScopeRule } from "./scopeRules";
import { ScopeValuePicker } from "./ScopeValuePicker";
import {
  ADD_EXCLUSION,
  ADD_INCLUSION,
  EXCLUDE_BY,
  INCLUDE_BY,
  PICK_A_BOX,
  PICK_VALUES,
  removeRuleLabel,
  RULE_EXAMPLE,
  RULE_LINE,
  SELECT_BOX_PLACEHOLDER,
} from "./setScopeCopy";
import styles from "./SetScopeModal.module.scss";

export interface ScopeRuleRowsProps {
  company: UserScopeCompany;
  rules: ScopeRule[];
  onChange: (rules: ScopeRule[]) => void;
  /** True once the user has tried to save, so a half-made row is not scolded before then. */
  showErrors: boolean;
  /**
   * The message for a required row left empty. Without it an empty required row is no error:
   * Set Scope lets an admin move past someone they are not ready to answer for.
   */
  requiredError?: (rule: ScopeRule) => string | undefined;
  /**
   * Show the required row's error as soon as it is empty, before any save is tried (the person's
   * Edit Scope, sketch `edge-scope-last-group`). Other rows still wait for `showErrors`.
   */
  showRequiredErrors?: boolean;
  /** Offered first in a required Clients row's values (the New User wizard's "All clients"). */
  allClientsOption?: { value: string; label: string };
  /** Passed to each box's picker to reword an option's label. */
  labelFor?: (box: WritableBox, value: string, label: string) => string;
  /** Passed to each box's picker: a picked value that can't be unpicked is drawn disabled. */
  lockedFor?: (box: WritableBox, value: string) => boolean;
}

/**
 * The entity card's body, as `entityCard` + `incGroup` draw it in the sketches: a line explaining
 * the same-type rule, then one row per limit, then Add Inclusion / Add Exclusion.
 *
 * A row is "limit by this box, to these values". A box the role requires arrives as a row that
 * cannot be removed; anything else the person's roles put in play is added on demand.
 */
export function ScopeRuleRows({
  company,
  rules,
  onChange,
  showErrors,
  requiredError,
  showRequiredErrors = false,
  allClientsOption,
  labelFor,
  lockedFor,
}: ScopeRuleRowsProps) {
  const update = (key: string, next: Partial<ScopeRule>) =>
    onChange(rules.map((rule) => (rule.key === key ? { ...rule, ...next } : rule)));

  return (
    <Flex direction="column" gap="var(--spacing-medium)">
      <div>
        <Text className={styles.ruleLine}>{RULE_LINE}</Text>
        <Text className={styles.ruleExample}>{RULE_EXAMPLE}</Text>
      </div>

      {rules.map((rule) => {
        const options = boxOptionsFor(company, rules, rule);
        return (
          <div key={rule.key} className={styles.ruleRow}>
            <InputDropdown
              label={rule.kind === "inclusion" ? INCLUDE_BY : EXCLUDE_BY}
              placeholder={SELECT_BOX_PLACEHOLDER}
              error={showErrors && !rule.required && rule.box === null ? PICK_A_BOX : false}
              searchable
              // A required box is what the role asks for; changing it would be answering a
              // different question from the one the person is blocked on.
              disabled={rule.required}
              items={options.map((box) => ({ value: box, label: boxLabel(box) }))}
              value={rule.box}
              onChange={(value) =>
                update(rule.key, { box: (value as WritableBox) ?? null, ids: [] })
              }
            />

            {rule.box && (
              <ScopeValuePicker
                tenantId={company.tenantId}
                tenantName={company.tenantName}
                box={rule.box}
                boxLabel={boxLabel(rule.box)}
                error={
                  rule.ids.length === 0
                    ? rule.required
                      ? showErrors || showRequiredErrors
                        ? requiredError?.(rule)
                        : undefined
                      : showErrors
                        ? PICK_VALUES
                        : undefined
                    : undefined
                }
                allOption={rule.required && rule.box === "clients" ? allClientsOption : undefined}
                labelFor={
                  labelFor && rule.box
                    ? (value, label) => labelFor(rule.box as WritableBox, value, label)
                    : undefined
                }
                isLocked={
                  lockedFor && rule.box
                    ? (value) => lockedFor(rule.box as WritableBox, value)
                    : undefined
                }
                value={rule.ids}
                onChange={(ids) => update(rule.key, { ids })}
              />
            )}

            <div className={styles.ruleActions}>
              {!rule.required && (
                <Button
                  type="text"
                  variant="text-destructive-primary"
                  size="base"
                  aria-label={removeRuleLabel(rule.box, rule.kind)}
                  onClick={() => onChange(rules.filter((entry) => entry.key !== rule.key))}
                >
                  <IconTrash size={16} />
                </Button>
              )}
            </div>
          </div>
        );
      })}

      <div className={styles.addRuleButtons}>
        <Button
          type="text"
          variant="text-secondary"
          size="small"
          iconLeft={<IconPlus size={16} />}
          isDisabled={noBoxLeftFor(company, rules, "inclusion")}
          onClick={() => onChange([...rules, newRule("inclusion")])}
        >
          {ADD_INCLUSION}
        </Button>
        <Button
          type="text"
          variant="text-secondary"
          size="small"
          iconLeft={<IconPlus size={16} />}
          isDisabled={noBoxLeftFor(company, rules, "exclusion")}
          onClick={() => onChange([...rules, newRule("exclusion")])}
        >
          {ADD_EXCLUSION}
        </Button>
      </div>
    </Flex>
  );
}
